// Uniform navigation grid + A* pathfinding used per squad. At squad scale (a few
// hundred models, tens of squads) fine-grid A* is plenty; no flow fields needed (GDD §8.2).

import type { Vec2 } from "./vec";

export class NavGrid {
  readonly cols: number;
  readonly rows: number;
  readonly cell: number;
  /** blocked[idx] = true means impassable (building footprints, map edge padding). */
  private blocked: Uint8Array;

  constructor(width: number, height: number, cell: number) {
    this.cell = cell;
    this.cols = Math.max(1, Math.ceil(width / cell));
    this.rows = Math.max(1, Math.ceil(height / cell));
    this.blocked = new Uint8Array(this.cols * this.rows);
  }

  idx(cx: number, cy: number): number {
    return cy * this.cols + cx;
  }

  inBounds(cx: number, cy: number): boolean {
    return cx >= 0 && cy >= 0 && cx < this.cols && cy < this.rows;
  }

  worldToCell(p: Vec2): { cx: number; cy: number } {
    return {
      cx: Math.min(this.cols - 1, Math.max(0, Math.floor(p.x / this.cell))),
      cy: Math.min(this.rows - 1, Math.max(0, Math.floor(p.y / this.cell))),
    };
  }

  cellToWorld(cx: number, cy: number): Vec2 {
    return { x: (cx + 0.5) * this.cell, y: (cy + 0.5) * this.cell };
  }

  setBlockedRect(x: number, y: number, w: number, h: number, val = true): void {
    const c0 = Math.max(0, Math.floor(x / this.cell));
    const r0 = Math.max(0, Math.floor(y / this.cell));
    const c1 = Math.min(this.cols - 1, Math.floor((x + w) / this.cell));
    const r1 = Math.min(this.rows - 1, Math.floor((y + h) / this.cell));
    for (let r = r0; r <= r1; r++)
      for (let c = c0; c <= c1; c++) this.blocked[this.idx(c, r)] = val ? 1 : 0;
  }

  isBlocked(cx: number, cy: number): boolean {
    if (!this.inBounds(cx, cy)) return true;
    return this.blocked[this.idx(cx, cy)] === 1;
  }

  /** Nearest passable cell to a world point (spiral search) — keeps orders robust. */
  nearestOpen(p: Vec2): { cx: number; cy: number } {
    const { cx, cy } = this.worldToCell(p);
    if (!this.isBlocked(cx, cy)) return { cx, cy };
    for (let r = 1; r < Math.max(this.cols, this.rows); r++) {
      for (let dy = -r; dy <= r; dy++)
        for (let dx = -r; dx <= r; dx++) {
          if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
          const nx = cx + dx;
          const ny = cy + dy;
          if (this.inBounds(nx, ny) && !this.isBlocked(nx, ny)) return { cx: nx, cy: ny };
        }
    }
    return { cx, cy };
  }

  /** A* returning a list of world-space waypoints from start to goal (empty if unreachable). */
  findPath(start: Vec2, goal: Vec2): Vec2[] {
    const s = this.nearestOpen(start);
    const g = this.nearestOpen(goal);
    const startIdx = this.idx(s.cx, s.cy);
    const goalIdx = this.idx(g.cx, g.cy);
    if (startIdx === goalIdx) return [this.cellToWorld(g.cx, g.cy)];

    const n = this.cols * this.rows;
    const came = new Int32Array(n).fill(-1);
    const gScore = new Float64Array(n).fill(Infinity);
    const fScore = new Float64Array(n).fill(Infinity);
    const open = new MinHeap();
    gScore[startIdx] = 0;
    fScore[startIdx] = this.heuristic(s.cx, s.cy, g.cx, g.cy);
    open.push(startIdx, fScore[startIdx]);

    const dirs = [
      [1, 0, 1],
      [-1, 0, 1],
      [0, 1, 1],
      [0, -1, 1],
      [1, 1, 1.4142],
      [1, -1, 1.4142],
      [-1, 1, 1.4142],
      [-1, -1, 1.4142],
    ];

    let iterations = 0;
    const maxIter = n * 4;
    while (open.size() > 0 && iterations++ < maxIter) {
      const current = open.pop();
      if (current === goalIdx) return this.reconstruct(came, current, g);
      const cx = current % this.cols;
      const cy = (current / this.cols) | 0;
      for (const [dx, dy, cost] of dirs) {
        const nx = cx + dx;
        const ny = cy + dy;
        if (!this.inBounds(nx, ny) || this.isBlocked(nx, ny)) continue;
        // Prevent cutting diagonal corners between two blocked cells.
        if (dx !== 0 && dy !== 0) {
          if (this.isBlocked(cx + dx, cy) && this.isBlocked(cx, cy + dy)) continue;
        }
        const ni = this.idx(nx, ny);
        const tentative = gScore[current] + cost;
        if (tentative < gScore[ni]) {
          came[ni] = current;
          gScore[ni] = tentative;
          fScore[ni] = tentative + this.heuristic(nx, ny, g.cx, g.cy);
          open.push(ni, fScore[ni]);
        }
      }
    }
    return [];
  }

  private heuristic(ax: number, ay: number, bx: number, by: number): number {
    const dx = Math.abs(ax - bx);
    const dy = Math.abs(ay - by);
    return (dx + dy) + (1.4142 - 2) * Math.min(dx, dy);
  }

  private reconstruct(came: Int32Array, current: number, g: { cx: number; cy: number }): Vec2[] {
    const path: Vec2[] = [];
    let c = current;
    while (c !== -1) {
      const cx = c % this.cols;
      const cy = (c / this.cols) | 0;
      path.push(this.cellToWorld(cx, cy));
      c = came[c];
    }
    path.reverse();
    path[path.length - 1] = this.cellToWorld(g.cx, g.cy);
    return simplify(path);
  }
}

/** Drop collinear intermediate waypoints so squads walk in clean straight lines. */
function simplify(path: Vec2[]): Vec2[] {
  if (path.length <= 2) return path;
  const out: Vec2[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const a = out[out.length - 1];
    const b = path[i];
    const c = path[i + 1];
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const bcx = c.x - b.x;
    const bcy = c.y - b.y;
    const cross = abx * bcy - aby * bcx;
    if (Math.abs(cross) > 1e-3) out.push(b);
  }
  out.push(path[path.length - 1]);
  return out;
}

/** Binary min-heap keyed by priority; stores cell indices. */
class MinHeap {
  private items: number[] = [];
  private prio: number[] = [];

  size(): number {
    return this.items.length;
  }

  push(item: number, priority: number): void {
    this.items.push(item);
    this.prio.push(priority);
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.prio[parent] <= this.prio[i]) break;
      this.swap(i, parent);
      i = parent;
    }
  }

  pop(): number {
    const top = this.items[0];
    const last = this.items.length - 1;
    this.swap(0, last);
    this.items.pop();
    this.prio.pop();
    let i = 0;
    const n = this.items.length;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let smallest = i;
      if (l < n && this.prio[l] < this.prio[smallest]) smallest = l;
      if (r < n && this.prio[r] < this.prio[smallest]) smallest = r;
      if (smallest === i) break;
      this.swap(i, smallest);
      i = smallest;
    }
    return top;
  }

  private swap(a: number, b: number): void {
    [this.items[a], this.items[b]] = [this.items[b], this.items[a]];
    [this.prio[a], this.prio[b]] = [this.prio[b], this.prio[a]];
  }
}
